import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight, Clock, Award } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CoursesPage = () => {
  const { t, language, isRTL } = useLanguage();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const response = await axios.get(`${API}/courses`);
        setCourses(response.data);
      } catch (error) {
        console.error('Error fetching courses:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCourses();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFCF8]">
        <div className="w-12 h-12 border-4 border-[#C5A059] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="bg-[#FDFCF8] min-h-screen pt-24">
      {/* Hero Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <div className={`${isRTL ? 'text-right' : 'text-left'} max-w-3xl`}>
            <p className="font-manrope text-xs md:text-sm uppercase tracking-[0.2em] text-[#C5A059] mb-4">
              {t.courses.subtitle}
            </p>
            <h1
              data-testid="courses-title"
              className={`${isRTL ? 'font-amiri' : 'font-playfair'} text-4xl md:text-6xl font-medium text-[#1A1A1A] mb-6`}
            >
              {t.courses.title}
            </h1>
            <p className={`${isRTL ? 'font-tajawal' : 'font-manrope'} text-lg text-[#666660] leading-relaxed`}>
              {language === 'en'
                ? 'Transform your passion into a profession with our comprehensive training programs led by international trainer Nina Ceban.'
                : 'حولي شغفك إلى مهنة مع برامجنا التدريبية الشاملة بقيادة المدربة الدولية نينا سيبان.'}
            </p>
          </div>
        </div>
      </section>

      {/* Courses Grid */}
      <section className="pb-20 md:pb-32">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <div className="space-y-16">
            {courses.map((course, index) => (
              <Card
                key={course.id}
                data-testid={`course-card-${index}`}
                className="overflow-hidden bg-white border border-[#E6E2D6]/50 hover:border-[#C5A059]/30 transition-all duration-500 hover:shadow-2xl hover:shadow-[#C5A059]/5"
              >
                <div className="grid grid-cols-1 lg:grid-cols-2">
                  {/* Image */}
                  <div className="relative aspect-[4/3] lg:aspect-auto overflow-hidden">
                    <img
                      src={course.image_url}
                      alt={language === 'en' ? course.title_en : course.title_ar}
                      className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                    />
                    {/* Duration Badge */}
                    <div className="absolute top-4 left-4 flex items-center gap-2 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full">
                      <Clock className="w-4 h-4 text-[#C5A059]" />
                      <span className="font-manrope text-sm font-medium text-[#1A1A1A]">
                        {course.duration}
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className={`p-8 lg:p-12 ${isRTL ? 'text-right' : 'text-left'}`}>
                    <CardHeader className="p-0 mb-6">
                      <CardTitle className={`${isRTL ? 'font-amiri' : 'font-playfair'} text-2xl md:text-3xl font-medium text-[#1A1A1A]`}>
                        {language === 'en' ? course.title_en : course.title_ar}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                      <p className={`${isRTL ? 'font-tajawal' : 'font-manrope'} text-[#666660] leading-relaxed mb-6`}>
                        {language === 'en' ? course.description_en : course.description_ar}
                      </p>

                      {/* Curriculum */}
                      <div className="mb-8">
                        <h4 className={`${isRTL ? 'font-amiri' : 'font-playfair'} text-lg font-medium text-[#1A1A1A] mb-4`}>
                          {t.courses.curriculum}
                        </h4>
                        <ul className="space-y-3">
                          {(language === 'en' ? course.curriculum_en : course.curriculum_ar).map((item, idx) => (
                            <li key={idx} className={`flex items-start gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                              <CheckCircle className="w-5 h-5 text-[#C5A059] flex-shrink-0 mt-0.5" />
                              <span className={`${isRTL ? 'font-tajawal' : 'font-manrope'} text-sm text-[#666660]`}>
                                {item}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Pricing */}
                      <div className="grid grid-cols-2 gap-4 mb-8">
                        <div className="bg-[#F3ECE4]/50 p-4 rounded-xl text-center">
                          <p className="font-manrope text-xs uppercase tracking-wider text-[#666660] mb-1">
                            {t.courses.groupPrice}
                          </p>
                          <p className="font-playfair text-2xl font-medium text-[#1A1A1A]">
                            {course.group_price}
                          </p>
                          <p className="font-manrope text-sm text-[#C5A059]">{t.courses.sar}</p>
                        </div>
                        <div className="bg-[#C5A059]/10 p-4 rounded-xl text-center border border-[#C5A059]/30">
                          <p className="font-manrope text-xs uppercase tracking-wider text-[#666660] mb-1">
                            {t.courses.individualPrice}
                          </p>
                          <p className="font-playfair text-2xl font-medium text-[#1A1A1A]">
                            {course.individual_price}
                          </p>
                          <p className="font-manrope text-sm text-[#C5A059]">{t.courses.sar}</p>
                        </div>
                      </div>

                      {/* Certificate Note */}
                      <div className={`flex items-center gap-3 mb-8 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <Award className="w-5 h-5 text-[#C5A059]" />
                        <p className={`${isRTL ? 'font-tajawal' : 'font-manrope'} text-sm text-[#666660]`}>
                          {t.courses.certificate}
                        </p>
                      </div>

                      {/* CTA */}
                      <Link to="/contact">
                        <Button
                          data-testid={`enroll-course-${index}`}
                          className="rounded-full bg-[#C5A059] text-white hover:bg-[#B08A3E] px-8 py-6 text-lg font-medium shadow-[0_4px_14px_0_rgba(197,160,89,0.39)] hover:shadow-[0_6px_20px_rgba(197,160,89,0.23)] hover:-translate-y-1 transition-all duration-300"
                        >
                          {t.courses.enrollNow}
                          <ArrowRight className={`w-5 h-5 ${isRTL ? 'mr-2 rotate-180' : 'ml-2'}`} />
                        </Button>
                      </Link>
                    </CardContent>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default CoursesPage;
